import React, { useState } from "react";
import Modal from "./Modal";
import { Button, Select } from "../Form";
import { BiChevronDown } from "react-icons/bi";
import { sortsDatas } from "../Datas";
import { HiOutlineCheckCircle } from "react-icons/hi";
import { toast } from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import { BaseUrl } from "../../BaseUrl";
import axios from "axios";

function AddStaffModal({ closeModal, isOpen, datas }) {
  const [gender, setGender] = useState(
    datas?.gender
      ? sortsDatas.genderFilter.find((g) => g.name === datas.gender) ||
          sortsDatas.genderFilter[0]
      : sortsDatas.genderFilter[0]
  );
  // Access options based on image (Admin, Manager, Member)
  const accessOptions = [
    { id: 1, name: "Select" },
    { id: 2, name: "Admin" },
    { id: 3, name: "Manager" },
    { id: 4, name: "Member" },
  ];

  const [access, setAccess] = useState(
    datas?.access
      ? accessOptions.find((a) => a.name === datas.access) || accessOptions[0]
      : accessOptions[0]
  );
  const [searchQuery, setSearchQuery] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      fullName: datas?.user?.title || datas?.fullName || "",
      phoneNumber: datas?.user?.phone || datas?.mobileNumber || "",
      email: datas?.user?.email || datas?.emailId || "",
    },
  });

  // Populate form when datas changes (for view/edit mode)
  React.useEffect(() => {
    if (datas && datas.id) {
      reset({
        fullName: datas?.user?.title || datas?.fullName || "",
        phoneNumber: datas?.user?.phone || datas?.mobileNumber || "",
        email: datas?.user?.email || datas?.emailId || "",
      });
      // Set gender if available
      if (datas?.gender) {
        const foundGender = sortsDatas.genderFilter.find(
          (g) => g.name === datas.gender
        );
        if (foundGender) setGender(foundGender);
      }
      // Set access if available (map role to access)
      if (datas?.role) {
        const roleMap = {
          2: "Manager",
          3: "Admin",
        };
        const accessName = roleMap[datas.role] || "Member";
        const foundAccess = accessOptions.find((a) => a.name === accessName);
        if (foundAccess) setAccess(foundAccess);
      }
    } else {
      // Reset form for new staff
      reset({
        fullName: "",
        phoneNumber: "",
        email: "",
      });
      setGender(sortsDatas.genderFilter[0]);
      setAccess(accessOptions[0]);
    }
  }, [datas, reset]);

  const onSubmit = async (formData) => {
    try {
      // Validate Access - must not be "Select"
      if (!access || access.name === "Select" || access.id === 1) {
        toast.error("Please select an Access level");
        return;
      }

      // Validate Gender - must not be "Gender..."
      if (!gender || gender.name === "Gender..." || gender.id === 1) {
        toast.error("Please select a Gender");
        return;
      }

      // Map access to staffRole (directly use the role value for backend)
      // Access ID: 2=Admin, 3=Manager, 4=Member
      const accessToRoleMap = {
        2: "admin", // Admin access -> staffRole: admin
        3: "manager", // Manager access -> staffRole: manager
        4: "member", // Member access -> staffRole: member
      };
      const role = accessToRoleMap[String(access.id)] || "member";

      const payload = {
        fullName: formData.fullName,
        mobileNumber: formData.phoneNumber,
        emailId: formData.email,
        gender: gender.name,
        role: role,
        inviteType: "staff", // Flag to indicate this is a staff invitation (email only)
      };

      if (datas && datas.id) {
        // Update existing staff (if API supports it)
        toast.error("Update functionality not implemented yet");
        return;
      }
      // console.log("payload-----",payload);
      // Get auth token
      const token = localStorage.getItem("token");

      // Invite new staff - send invitation
      const response = await axios.post(
        `${BaseUrl}/facility/receptions/invite`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        toast.success("Staff invitation sent successfully!");
        closeModal();
        // Refresh the list - you might want to add a refresh callback
        window.location.reload(); // Temporary solution
      } else {
        toast.error(response.data.message || "Failed to send invitation");
      }
    } catch (error) {
      console.log("STATUS:", error.response?.status);
      console.log("BACKEND MESSAGE:", error.response?.data);
      toast.error(
        error.response?.data?.message ||
          "Failed to send invitation. Please try again."
      );
    }
  };

  return (
    <Modal
      closeModal={closeModal}
      isOpen={isOpen}
      title={datas?.id ? "View Staff" : "Add Staff"}
      width={"max-w-3xl"}
    >
      <form onSubmit={handleSubmit(onSubmit)} className='flex-colo gap-6'>
        {/* Search bar */}
        <div className='group mt-3 flex w-[70%] flex-row items-center justify-between self-start rounded-lg border border-border bg-dry p-3 focus-within:border focus-within:border-[#0095D9]'>
          <img
            src={require("../../Assets/images/search.png")}
            className='mr-2 h-6 w-6'
          />
          <input
            type='text'
            placeholder='Search'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='font-md w-full bg-transparent text-sm text-black focus:outline-none'
          />
        </div>

        {/* Form fields in 2 columns */}
        <div className='grid w-full items-end gap-4 sm:grid-cols-2'>
          {/* Left Column */}
          <FormInput
            name='fullName'
            label='Full Name *'
            placeholder='Full Name'
            control={control}
            rules={{ required: "Full name is required" }}
          />

          <FormInput
            name='phoneNumber'
            label='Phone Number'
            placeholder='Phone Number'
            control={control}
            rules={{}}
          />

          <div className='mt-3 flex w-[90%] flex-col gap-3'>
            <p className='text-sm font-bold text-black'>Access</p>
            <Select
              selectedPerson={access}
              setSelectedPerson={setAccess}
              datas={accessOptions}
            >
              <div className='flex-btn w-full rounded-lg border border-border p-4 text-sm font-light text-textGray focus:border focus:border-[#0095D9]'>
                {access?.name} <BiChevronDown className='text-xl' />
              </div>
            </Select>
          </div>

          {/* Right Column */}
          <FormInput
            name='email'
            label='Email'
            placeholder='Email'
            control={control}
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            }}
          />

          <div className='mt-3 flex w-[90%] flex-col gap-3'>
            <p className='text-sm font-bold text-black'>Gender</p>
            <Select
              selectedPerson={gender}
              setSelectedPerson={setGender}
              datas={sortsDatas.genderFilter}
            >
              <div className='flex-btn w-full rounded-lg border border-border p-4 text-sm font-light text-textGray focus:border focus:border-[#0095D9]'>
                {gender?.name} <BiChevronDown className='text-xl' />
              </div>
            </Select>
          </div>
        </div>

        {/* Buttons */}
        <div className='grid w-full gap-4 sm:grid-cols-2'>
          <button
            type='button'
            onClick={closeModal}
            className='rounded-lg bg-[#B8B8B8] p-4 text-sm font-light text-white'
          >
            Cancel
          </button>
          <Button
            type='submit'
            label={datas?.id ? "Update" : "Invite"}
            Icon={HiOutlineCheckCircle}
          />
        </div>
      </form>
    </Modal>
  );
}

// Reusable form input component using react-hook-form
const FormInput = ({ name, label, placeholder, control, rules = {} }) => {
  return (
    <div className='mt-3 w-[90%] text-sm'>
      <label className='text-sm font-bold text-black'>{label}</label>
      <Controller
        name={name}
        control={control}
        rules={rules}
        defaultValue=''
        render={({ field, fieldState: { error } }) => (
          <>
            <input
              {...field}
              type='text'
              placeholder={placeholder}
              className={`mt-3 w-full rounded-lg border border-border bg-transparent p-4 text-sm font-light focus:border focus:border-subMain ${
                error ? "border-red-500" : ""
              }`}
            />
            {error && (
              <p className='mt-1 text-xs text-red-500'>{error.message}</p>
            )}
          </>
        )}
      />
    </div>
  );
};

export default AddStaffModal;
